import { SxProps, Theme } from "@mui/material";
import delegationTokens, {
  delegationResponsiveTokens,
} from "./Delegation.tokens";
import { PHONE_SM_QUERY } from "screens/Settings/Settings.tokens";

const { card, header, toggle, field, dateRange, primaryButton } =
  delegationTokens;

const { phone, tablet } = delegationResponsiveTokens;

/**
 * Field geometry every control on the card shares. `xs` carries the reference's
 * 390 column (`PHONE_SM_QUERY` narrows it to 375), `sm` the 834 tablet and `md`
 * the untouched desktop values.
 */
const fieldHeight = {
  xs: phone.field.height.std,
  sm: tablet.field.height,
  md: field.height,
};
const fieldRadius = {
  xs: field.radius,
  sm: tablet.field.radius,
  md: field.radius,
};
const fieldFontSize = {
  xs: phone.field.fontSize.std,
  sm: tablet.field.fontSize,
  md: field.fontSize,
};
const fieldPaddingX = {
  xs: field.paddingX,
  sm: tablet.field.paddingX,
  md: field.paddingX,
};

const delegationStyles = {
  card: {
    bgcolor: "background.paper",
    border: `1px solid ${card.border}`,
    borderRadius: card.radius,
    boxShadow: card.shadow,
    boxSizing: "border-box",
    p: { xs: phone.card.padding.std, sm: tablet.card.padding, md: card.padding },
    display: "flex",
    flexDirection: "column",
    gap: { xs: phone.card.gap.std, sm: tablet.card.gap, md: card.gap },
    [PHONE_SM_QUERY]: {
      padding: phone.card.padding.sm,
      gap: phone.card.gap.sm,
    },
  } as SxProps<Theme>,

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: { xs: phone.header.gap, sm: tablet.header.gap, md: header.gap },
  } as SxProps<Theme>,

  cardHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: header.textGap,
    minWidth: 0,
  } as SxProps<Theme>,

  /** Loading variant of `cardHeaderText` — grows so the lines fill the row. */
  skeletonHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: header.textGap,
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  cardTitle: {
    color: header.titleColor,
    fontSize: {
      xs: phone.header.titleFontSize.std,
      sm: tablet.header.titleFontSize,
      md: header.titleFontSize,
    },
    [PHONE_SM_QUERY]: { fontSize: phone.header.titleFontSize.sm },
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  cardDescription: {
    color: header.descriptionColor,
    fontSize: {
      xs: phone.header.descriptionFontSize.std,
      sm: tablet.header.descriptionFontSize,
      md: header.descriptionFontSize,
    },
    lineHeight: header.descriptionLineHeight,
    [PHONE_SM_QUERY]: { fontSize: phone.header.descriptionFontSize.sm },
  } as SxProps<Theme>,

  toggleWrapper: {
    ml: "auto",
    mt: toggle.marginTop,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: toggle.lockGap,
  } as SxProps<Theme>,

  toggleTrack: (checked: boolean) =>
    ({
      position: "relative",
      flexShrink: 0,
      width: toggle.width,
      height: toggle.height,
      p: 0,
      borderRadius: "999px",
      bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      transition: toggle.transition,
      "&:hover": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
      "&.Mui-disabled": {
        bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "2px",
      },
    }) as SxProps<Theme>,

  toggleKnob: (checked: boolean) =>
    ({
      position: "absolute",
      top: toggle.knobTop,
      left: 0,
      width: toggle.knobSize,
      height: toggle.knobSize,
      borderRadius: "50%",
      bgcolor: toggle.knobBg,
      boxShadow: toggle.knobShadow,
      transform: checked ? toggle.knobOffsetOn : toggle.knobOffsetOff,
      transition: toggle.knobTransition,
    }) as SxProps<Theme>,

  toggleLock: {
    fontSize: toggle.lockSize,
    color: toggle.lockColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /**
   * Two equal columns; the date range spans both. Dims as a whole while the
   * feature is off — the controls keep their normal chrome, exactly as in the
   * design, so no separate disabled palette is needed.
   */
  fieldGrid: (enabled: boolean) =>
    ({
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        sm: "minmax(0, 1fr) minmax(0, 1fr)",
      },
      rowGap: {
        xs: phone.field.rowGap.std,
        sm: tablet.field.rowGap,
        md: field.rowGap,
      },
      columnGap: { sm: tablet.field.columnGap, md: field.columnGap },
      [PHONE_SM_QUERY]: { rowGap: phone.field.rowGap.sm },
      opacity: enabled ? 1 : field.disabledOpacity,
      transition: field.opacityTransition,
    }) as SxProps<Theme>,

  fullWidthField: {
    gridColumn: "1 / -1",
  } as SxProps<Theme>,

  fieldLabel: {
    color: field.labelColor,
    fontSize: {
      xs: phone.field.labelFontSize.std,
      sm: tablet.field.labelFontSize,
      md: field.labelFontSize,
    },
    [PHONE_SM_QUERY]: { fontSize: phone.field.labelFontSize.sm },
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
    ml: 0,
  } as SxProps<Theme>,

  textField: {
    "& .MuiOutlinedInput-root": {
      height: fieldHeight,
      p: 0,
      borderRadius: fieldRadius,
      bgcolor: field.bg,
      [PHONE_SM_QUERY]: { height: phone.field.height.sm },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.border,
        borderWidth: "1px",
      },
      "&:hover": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
      },
      "&.Mui-focused": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: field.focusBorder,
          borderWidth: "1px",
        },
      },
      "&.Mui-disabled": {
        bgcolor: field.bg,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "error.main",
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      p: {
        xs: `0 ${field.paddingX}`,
        sm: `0 ${tablet.field.paddingX}`,
        md: `0 ${field.paddingX}`,
      },
      fontSize: fieldFontSize,
      fontWeight: field.fontWeight,
      color: field.color,
      [PHONE_SM_QUERY]: { fontSize: phone.field.fontSize.sm },
      "&::placeholder": {
        color: field.placeholder,
        opacity: 1,
        fontSize: fieldFontSize,
        fontWeight: field.fontWeight,
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: field.color,
        color: field.color,
      },
    },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "4px",
      fontSize: field.errorFontSize,
    },
  } as SxProps<Theme>,

  /**
   * Trigger of `CustomDateRangePicker`, restyled to the same 40px pill as the
   * text fields. `isEmpty` renders the "All time" fallback in placeholder ink.
   */
  dateRangeField: (isEmpty: boolean) =>
    ({
      minWidth: 0,
      width: "100%",
      "& .MuiOutlinedInput-root": {
        height: fieldHeight,
        px: fieldPaddingX,
        gap: dateRange.gap,
        borderRadius: fieldRadius,
        bgcolor: field.bg,
        cursor: "pointer",
        [PHONE_SM_QUERY]: { height: phone.field.height.sm },
        "& .MuiOutlinedInput-notchedOutline": {
          border: `1px solid ${field.border}`,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: `1px solid ${field.border}`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: `1px solid ${field.focusBorder}`,
        },
        "&.Mui-disabled": {
          bgcolor: field.bg,
          "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${field.border}`,
          },
        },
      },
      "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
        p: "0 !important",
        height: "100%",
        boxSizing: "border-box",
        fontSize: fieldFontSize,
        fontWeight: field.fontWeight,
        color: isEmpty ? field.placeholder : field.color,
        textOverflow: "ellipsis",
        [PHONE_SM_QUERY]: { fontSize: phone.field.fontSize.sm },
      },
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: isEmpty ? field.placeholder : field.color,
        color: isEmpty ? field.placeholder : field.color,
      },
    }) as SxProps<Theme>,

  dateRangeIcon: {
    width: dateRange.iconSize,
    height: dateRange.iconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  fieldError: {
    ml: 0,
    mt: "4px",
    fontSize: field.errorFontSize,
  } as SxProps<Theme>,

  buttonRow: (enabled: boolean) =>
    ({
      display: "flex",
      opacity: enabled ? 1 : field.disabledOpacity,
      transition: field.opacityTransition,
    }) as SxProps<Theme>,

  primaryButton: {
    height: {
      xs: phone.primaryButton.height.std,
      sm: tablet.primaryButton.height,
      md: primaryButton.height,
    },
    px: {
      xs: phone.primaryButton.paddingX.std,
      sm: tablet.primaryButton.paddingX,
      md: primaryButton.paddingX,
    },
    borderRadius: {
      xs: primaryButton.radius,
      sm: tablet.primaryButton.radius,
      md: primaryButton.radius,
    },
    bgcolor: primaryButton.bg,
    color: primaryButton.color,
    fontSize: {
      xs: phone.primaryButton.fontSize.std,
      sm: tablet.primaryButton.fontSize,
      md: primaryButton.fontSize,
    },
    [PHONE_SM_QUERY]: {
      height: phone.primaryButton.height.sm,
      paddingLeft: phone.primaryButton.paddingX.sm,
      paddingRight: phone.primaryButton.paddingX.sm,
      fontSize: phone.primaryButton.fontSize.sm,
    },
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
    "&.Mui-disabled": {
      bgcolor: primaryButton.disabledBg,
      color: primaryButton.disabledColor,
      cursor: "not-allowed",
      pointerEvents: "auto",
    },
    "& .MuiButton-endIcon": {
      ml: "8px",
      "& > *:first-of-type": { fontSize: primaryButton.lockSize },
    },
  } as SxProps<Theme>,
};

export default delegationStyles;
